import React from "react";
import { Box, Container, Typography, Divider } from "@mui/material";
import CostControlDiagram from "../assets/CostControlDiagram.png";
import CostControl from "../assets/CostControl.png";
import { Helmet } from "react-helmet";

export default function CostControlSection() {
  return (
    <>
      <Helmet>
        <title>Cost Control Management - Namami India Support Services</title>
        <meta
          name="description"
          content="Optimize operational efficiency with Namami India's cost control management — reducing expenses through smart resource utilization, maintenance planning, and process innovation."
        />
        <meta
          name="keywords"
          content="Namami India, cost control, cost optimization, facility management, expense reduction, operational efficiency"
        />
        <meta property="og:title" content="Cost Control Management - Namami India" />
        <meta
          property="og:description"
          content="Namami India's cost control strategies help organizations reduce operational expenses while enhancing service quality and sustainability."
        />
        <meta
          property="og:image"
          content="https://www.namamiindia.co.in/assets/namami-logo.png"
        />
        <meta
          property="og:url"
          content="https://www.namamiindia.co.in/cost-control"
        />
        <link rel="canonical" href="https://www.namamiindia.co.in/cost-control" />
      </Helmet>

      {/* === Section 1: Main Overview === */}
      <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: "#ffffff" }}>
        <Container
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 6, md: 8 },
          }}
        >
          {/* === Left Image === */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={CostControlDiagram}
              alt="Cost Control Diagram"
              sx={{
                width: { xs: "90%", sm: "80%", md: "100%" },
                maxWidth: 520,
                borderRadius: 0,
                boxShadow: "none",
                objectFit: "contain",
                transition: "transform 0.6s ease",
                "&:hover": { transform: "scale(1.05)" },
              }}
            />
          </Box>

          {/* === Right Text === */}
          <Box
            sx={{
              flex: 1,
              textAlign: { xs: "center", md: "left" },
              maxWidth: 600,
            }}
          >
            <Typography
              variant="h4"
              fontWeight={800}
              mb={2}
              sx={{
                background: "linear-gradient(90deg, #1565c0, #004ba0)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Cost Control Management
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.9, fontSize: "1.1rem", mb: 3 }}
            >
              Cost control is not just about cutting expenses — it’s about{" "}
              <strong>strategic optimization</strong>. At Namami India, we
              integrate intelligent systems and human expertise to deliver
              financially efficient facility management without compromising
              quality. Our goal is to make every resource — from manpower to
              machinery — work smarter, not harder.
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.9 }}
            >
              Through meticulous budgeting, preventive maintenance scheduling,
              and performance benchmarking, we ensure that operations remain
              sustainable, transparent, and resilient to future challenges.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* === Section 2: Hero / Inverse Highlight Section === */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          background: "linear-gradient(180deg, #0d47a1, #1565c0)",
          textAlign: "center",
          color: "white",
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h4"
            fontWeight={800}
            mb={3}
            sx={{
              letterSpacing: "0.5px",
              textShadow: "0 2px 6px rgba(0,0,0,0.3)",
            }}
          >
            The Power of Intelligent Cost Optimization
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: "1.15rem",
              lineHeight: 1.9,
              opacity: 0.95,
            }}
          >
            Our cost control framework is designed around{" "}
            <strong>data-driven decision-making</strong> and{" "}
            <strong>continuous improvement</strong>. From energy audits to
            vendor performance analytics, every insight helps us enhance
            productivity and sustainability. We believe in transforming
            traditional facilities into{" "}
            <strong>efficient, performance-oriented ecosystems</strong> that
            save money, time, and energy — all while maintaining world-class
            service quality.
          </Typography>

          <Divider
            sx={{
              my: 5,
              backgroundColor: "rgba(255,255,255,0.4)",
              width: "60%",
              mx: "auto",
            }}
          />

          <Typography variant="h6" fontWeight={600}>
            “Efficiency is achieved not by reducing effort — but by multiplying
            impact.”
          </Typography>
        </Container>
      </Box>

      {/* === Section 3: Bottom Chart === */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={CostControl}
          alt="Cost Control Analysis Chart"
          sx={{
            width: { xs: "85%", sm: "70%", md: "50%" },
            borderRadius: 0,
            boxShadow: "none",
            objectFit: "contain",
          }}
        />
      </Box>
    </>
  );
}
